import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useTenant } from '@/contexts/TenantProvider'

export interface FlashOpening {
  id: string
  stylist_id: string
  stylist_name?: string
  start_time: string
  end_time: string
  duration_minutes: number
  status: string
  created_at: string
}

/**
 * Fetches available gap slots ("Flash Openings") for the current tenant.
 * These are Phase-P idle windows where stylists are free for express services.
 *
 * @see PERD v10.0 §6.6 — The "Service Sandwich" & Tetris Scheduler
 */
export function useFlashOpenings(date?: string) {
  const { tenant } = useTenant()
  const tenantId = tenant?.id

  return useQuery<FlashOpening[]>({
    queryKey: ['flash-openings', tenantId, date],
    queryFn: async () => {
      const query = supabase
        .from('gap_slots')
        .select(
          `
          *,
          stylists!inner(name)
        `
        )
        .eq('tenant_id', tenantId!)
        .eq('status', 'available')
        .gte('start_time', date || new Date().toISOString().slice(0, 10))
        .order('start_time', { ascending: true })

      const { data, error } = await query
      if (error) throw error

      return (data ?? []).map((slot) => {
        const stylistData = slot.stylists as unknown as { name: string } | undefined
        const startMs = new Date(slot.start_time).getTime()
        const endMs = new Date(slot.end_time).getTime()
        const durationMinutes = Math.round((endMs - startMs) / 60000)

        return {
          id: slot.id,
          stylist_id: slot.stylist_id,
          stylist_name: stylistData?.name,
          start_time: slot.start_time,
          end_time: slot.end_time,
          status: slot.status,
          duration_minutes: durationMinutes,
          created_at: slot.created_at,
        }
      })
    },
    enabled: !!tenantId,
    refetchInterval: 60_000, // refresh every minute for real-time feel
  })
}
